import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type PendingCredit } from '../api';
import { date, money, nip, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Kolejka kredytów do zatwierdzenia (E11-S5).
 *
 * Kredyt powstaje, gdy korekta przychodu w dół zmniejsza opłatę za rozliczony miesiąc. Nie
 * wypłacamy go automatycznie: cofanie faktur korektami byłoby wtedy sposobem na odzyskanie
 * pieniędzy bez kontroli. Operator widzi, co się zmieniło — przychód i opłatę „było → jest" —
 * i decyduje. Odmowa wymaga uzasadnienia; przy zatwierdzeniu też warto je zapisać.
 */
export function Credits({
  t,
  locale,
  onCompany,
  onOffice,
  onChanged,
}: {
  t: Dictionary;
  locale: Locale;
  onCompany: (companyId: string) => void;
  onOffice: (officeId: string) => void;
  /** Po decyzji licznik przy pozycji nawigacji ma się zgadzać z listą. */
  onChanged: (pending: number) => void;
}) {
  const [items, setItems] = useState<PendingCredit[] | null>(null);
  const [deciding, setDeciding] = useState<{ id: string; approve: boolean; note: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await api.credits();
      setItems(loaded);
      onChanged(loaded.length);
    } catch (failure) {
      setItems([]);
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [onChanged]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide() {
    if (!deciding) return;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.decide(deciding.id, deciding.approve, deciding.note.trim() || null);
      setDeciding(null);
      setDone(true);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.credits.title}</h3>
          <p>{t.credits.lead}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {done && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.credits.decided}</b>
          </div>
        </div>
      )}

      {items === null && <p className="muted">{t.loading}</p>}
      {items && items.length === 0 && (
        <div className="panel">
          <p>{t.credits.empty}</p>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="panel">
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th className="wrap">{t.credits.company}</th>
                  <th>{t.credits.period}</th>
                  <th className="n wrap">{t.credits.revenue}</th>
                  <th className="n wrap">{t.credits.fee}</th>
                  <th className="n">{t.credits.amount}</th>
                  <th>{t.credits.waiting}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map(({ adjustment, company, office }) => (
                  <tr key={adjustment.id}>
                    <td className="wrap">
                      {company ? (
                        <>
                          <button type="button" className="btn ghost sm" onClick={() => onCompany(company.id)}>
                            <b>{company.name}</b>
                          </button>
                          <div className="hint mono">{nip(company.nip)}</div>
                        </>
                      ) : office ? (
                        <>
                          <button type="button" className="btn ghost sm" onClick={() => onOffice(office.id)}>
                            <b>{office.name}</b>
                          </button>
                          <div className="hint">
                            {t.credits.office} · <span className="mono">{nip(office.nip)}</span>
                          </div>
                        </>
                      ) : null}
                    </td>
                    <td className="mono">{adjustment.period}</td>
                    <td className="n mono">
                      {money(adjustment.previousRevenue, locale)} → {money(adjustment.currentRevenue, locale)}
                    </td>
                    <td className="n mono">
                      {money(adjustment.previousFee, locale)} → {money(adjustment.currentFee, locale)}
                    </td>
                    <td className="n mono">
                      <b>{money(adjustment.amount, locale)}</b>
                    </td>
                    <td>{date(adjustment.createdAt, locale)}</td>
                    <td className="act">
                      <div className="row">
                        <button
                          type="button"
                          className="btn sm"
                          disabled={busy}
                          onClick={() => setDeciding({ id: adjustment.id, approve: true, note: '' })}
                        >
                          {t.credits.approve}
                        </button>
                        <button
                          type="button"
                          className="btn ghost sm"
                          disabled={busy}
                          onClick={() => setDeciding({ id: adjustment.id, approve: false, note: '' })}
                        >
                          {t.credits.reject}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deciding && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{deciding.approve ? t.credits.approve : t.credits.reject}</h4>
              <p>{t.credits.noteHint}</p>
            </div>
          </div>
          <label className="fld">
            <span>{t.credits.note}</span>
            <textarea
              className="inp"
              rows={3}
              value={deciding.note}
              onChange={(event) => setDeciding({ ...deciding, note: event.target.value })}
            />
          </label>
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busy || (!deciding.approve && deciding.note.trim() === '')}
              onClick={() => void decide()}
            >
              {deciding.approve ? t.credits.approve : t.credits.reject}
            </button>
            <button type="button" className="btn plain" disabled={busy} onClick={() => setDeciding(null)}>
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}
